'use client';

import { ReactNode } from 'react';

import { Tooltip } from 'components/app-components/ui/tooltip';
import { cn } from 'components/app-components/ui/utils';
import { useIsNativeApp } from 'hooks/useIsNativeApp';

const DEFAULT_MESSAGE = 'Available on the web app — open app.getthera.com on your computer to do this.';

interface WebOnlyActionProps {
  children: ReactNode;
  /** Tooltip text shown in the app explaining where to do this instead. */
  message?: ReactNode;
  /** Extra classes for the disabled wrapper (e.g. to control inline vs block). */
  className?: string;
}

/**
 * Wrap any control/feature that should be web-only.
 *
 * - On the web: renders children unchanged.
 * - Inside the native app: greys them out, blocks interaction, and shows a
 *   tooltip pointing the user to the web app.
 *
 * The `[&_*]:pointer-events-none` blocks clicks on everything inside while the
 * Tooltip's wrapping trigger still receives hover/tap, so the hint shows.
 *
 * NOTE: this is a UX gate, not a security boundary. Anything that must be truly
 * blocked also has to be enforced on the backend.
 */
export function WebOnlyAction({ children, message = DEFAULT_MESSAGE, className }: WebOnlyActionProps) {
  const isNative = useIsNativeApp();

  if (!isNative) return children;

  return (
    <Tooltip content={message}>
      <div aria-disabled className={cn('cursor-not-allowed opacity-50 [&_*]:pointer-events-none', className)}>
        {children}
      </div>
    </Tooltip>
  );
}
